import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useCallback, useMemo, useRef } from "react";
import { usePopover } from "../../../../../utils/hooks/use-popover";
import type { DataTableCellNode } from "../../nodes/data-table-cell-node";
import {
  INSERT_DATA_TABLE_COLUMN_COMMAND,
  INSERT_DATA_TABLE_ROW_COMMAND,
} from "../../types/commands";
import { BackgroundColourButton } from "./buttons/background-colour-button";
import { DeleteColumnButton } from "./buttons/delete-column-button";
import { DeleteRowButton } from "./buttons/delete-row-button";
import { InsertColumnButtons } from "./buttons/insert-column-buttons";
import { InsertRowButtons } from "./buttons/insert-row-buttons";

export const DataTablePopoverMenu: FC<{
  tableCellNode: DataTableCellNode;
  onClick: () => void;
}> = ({ tableCellNode, onClick }) => {
  const [editor] = useLexicalComposerContext();
  const popover = usePopover<HTMLButtonElement>();
  const popoverInputRef = useRef<HTMLInputElement>(null);

  const handleInsertColumn = useCallback(
    (insertAfter: boolean) => {
      editor.dispatchCommand(INSERT_DATA_TABLE_COLUMN_COMMAND, { insertAfter });
      onClick();
      return;
    },
    [editor, onClick],
  );

  const handleInsertRow = useCallback(
    (insertAfter: boolean) => {
      editor.dispatchCommand(INSERT_DATA_TABLE_ROW_COMMAND, {
        position: insertAfter ? "below" : "above",
      });
      onClick();
    },
    [editor, onClick],
  );

  const initialColour = useMemo(
    () => editor.read(() => tableCellNode.getBackgroundColor() ?? ""),
    [editor, tableCellNode],
  );

  return (
    <>
      {/* eslint-disable-next-line react-hooks/refs */}
      <Box display="flex" flexDirection="column" ref={popover.anchorRef}>
        <BackgroundColourButton
          initialColour={initialColour}
          nodeKey={tableCellNode.getKey()}
          popoverInputRef={popoverInputRef}
          // eslint-disable-next-line react-hooks/refs
          popover={popover}
        />
        <InsertRowButtons handleInsertRow={handleInsertRow} />
        <InsertColumnButtons handleInsertColumn={handleInsertColumn} />
        <DeleteRowButton onClick={onClick} />
        <DeleteColumnButton onClick={onClick} />
      </Box>
    </>
  );
};
